import { Fragment, useMemo } from 'react';
import { Steps } from '@packages/ui/patterns';
import { Stack, Section } from '@packages/ui/layout';
import { Typography } from '@packages/ui/primitives';
import { rem } from '@packages/ui/tokens';
import type { RenderBlockFn, BlockRendererSharedProps } from '../../types/blocks';
import type { WizardBlockV2, WizardStepV2 } from './types';
import { useWizardOrchestratorV2 } from './useWizardOrchestratorV2';
import { WizardChannelProvider } from './WizardChannelProvider';
import { WizardButtonBar } from './ui/WizardButtonBar';

export interface WizardBlockRendererV2Props extends BlockRendererSharedProps {
  block: WizardBlockV2;
  renderBlock: RenderBlockFn;
}

/**
 * Auto-flatten array-wrapped form data for display blocks.
 *
 * Form schemas with `type: "array"` (e.g. create mode with `items: [{ ... }]`)
 * store values inside `{ items: [{ field1, field2, ... }] }`. View schemas
 * expect flat keys like `{ field1, field2, ... }`.
 *
 * This function transparently unwraps `items[0]` and merges it with any
 * top-level keys, so views/reviews render correctly without handler intervention.
 * For form blocks (back navigation), data is returned as-is to preserve the
 * array structure the form expects.
 */
function flattenForDisplay(data: Record<string, unknown>): Record<string, unknown> {
  if (!Array.isArray(data.items) || data.items.length === 0) return data;
  const firstItem = data.items[0] as Record<string, unknown>;
  if (typeof firstItem !== 'object' || firstItem === null) return data;
  return { ...data, ...firstItem };
}

/**
 * Resolves block data for the current step.
 *
 * Priority:
 * 1. Own step data (e.g. form values saved via setStepData on this step's ID) — for Back navigation
 * 2. Merged data from ALL previous steps — for views/reviews that display collected data
 * 3. No data — block fetches its own (first visit, no prior steps)
 */
function resolveStepBlock(
  step: WizardStepV2,
  stepData: Record<string, Record<string, unknown>>,
): WizardStepV2['block'] {
  const ownData = stepData[step.id];
  if (ownData) {
    return { ...step.block, data: { ...ownData, __wizardRestore: true } } as WizardStepV2['block'];
  }

  const stepIds = Object.keys(stepData);
  if (stepIds.length === 0) {
    return step.block;
  }

  const isDisplayBlock = step.block.$type === 'view';
  const merged: Record<string, unknown> = {};
  for (const key of stepIds) {
    const val = stepData[key];
    Object.assign(merged, isDisplayBlock ? flattenForDisplay(val) : val);
  }
  if (Object.keys(merged).length === 0) return step.block;
  return { ...step.block, data: merged } as WizardStepV2['block'];
}

export function WizardBlockRendererV2({
  block,
  renderBlock,
  params,
  meta,
  onSuccess,
  onError,
  onCancel,
}: WizardBlockRendererV2Props): JSX.Element {
  const orch = useWizardOrchestratorV2({
    block,
    onSuccess,
    onCancel,
  });

  const stepShared: BlockRendererSharedProps = useMemo(
    () => ({
      params,
      meta: orch.meta,
      onSuccess: undefined,
      onError,
      onCancel: undefined,
    }),
    [params, orch.meta, onError],
  );

  return (
    <WizardChannelProvider
      wizardId={orch.wizardId}
      activeChannel={orch.activeChannel}
    >
      <Section title={block.title} semanticId={block.semanticId}>
        <Stack gap={rem(24)}>
          <Steps
            steps={orch.visibleSteps.map((step) => ({ label: step.label }))}
            currentStep={orch.currentStepIndex}
            variant='compact'
            semanticId={block.semanticId ?? 'wizardSteps'}
            onStepClick={(index) => {
              const step = orch.visibleSteps[index];
              if (step && index <= orch.currentStepIndex) {
                orch.goToStep(step.id);
              }
            }}
          />

          {orch.error != null && (
            <Typography variant='body' color='error'>
              {orch.error}
            </Typography>
          )}

          {orch.currentStep != null && (() => {
            const resolvedBlock = resolveStepBlock(orch.currentStep, orch.stepData);
            return (
              <Fragment key={orch.currentStep.id}>
                {renderBlock(resolvedBlock, stepShared)}
              </Fragment>
            );
          })()}

          <WizardButtonBar
            buttons={orch.buttons}
            onNext={orch.handleNext}
            onBack={orch.handleBack}
            onCancel={orch.handleCancel}
            onSecondaryClick={orch.handleSecondaryClick}
          />
        </Stack>
      </Section>
    </WizardChannelProvider>
  );
}
